import type { AppVersionState } from '@/lib/api';

type BadgeIntent = 'success' | 'warning' | 'danger' | 'neutral';

/**
 * Maps a raw version state to a label + badge intent, mirroring the OCP CLI's
 * `formatVersionState` so the portal and CLI read the same (RUNNING -> "PUBLISHED",
 * PUBLISHED -> "BUILT", etc.). green=success, yellow=warning, red=danger, gray=neutral.
 */
const STATE_DISPLAY: Record<AppVersionState, { label: string; intent: BadgeIntent }> = {
  NEW: { label: 'NEW', intent: 'warning' },
  PUBLISHED: { label: 'BUILT', intent: 'warning' },
  BUILD_FAILED: { label: 'BUILD_FAILED', intent: 'danger' },
  STARTING: { label: 'PUBLISHING', intent: 'warning' },
  START_FAILED: { label: 'PUBLISHING_FAILED', intent: 'danger' },
  RUNNING: { label: 'PUBLISHED', intent: 'success' },
  UPGRADING_RUNTIME: { label: 'UPGRADING_RUNTIME', intent: 'warning' },
  STOPPING: { label: 'UNPUBLISHING', intent: 'warning' },
  STOP_FAILED: { label: 'UNPUBLISHING_FAILED', intent: 'danger' },
  STOPPED: { label: 'UNPUBLISHED', intent: 'danger' },
  ABANDONED: { label: 'ABANDONED', intent: 'neutral' },
};

export function formatVersionState(state: AppVersionState): { label: string; intent: BadgeIntent } {
  return STATE_DISPLAY[state] ?? { label: 'UNKNOWN', intent: 'danger' };
}

/**
 * States the portal presents as "unpublished", sent to rivendell as `excludeStates` to keep
 * them out of the versions tab (OCP-2203). The API takes any set of states and has no opinion
 * about which are noise — that call is made here, next to the STATE_DISPLAY row that justifies
 * it (STOPPED is the state this portal labels UNPUBLISHED).
 */
export const UNPUBLISHED_STATES: readonly AppVersionState[] = ['STOPPED'];
